import {Component, EventEmitter, Input, OnDestroy, OnInit, Output, TemplateRef, ViewChild} from '@angular/core';
import {
    ECEntitySubtype,
    ECJuridicalType, ECLandRegistryData,
    ECLegalForm,
    ECMember,
    ECMemberAccessType,
    ECMemberParticipationType,
    ECMemberPersonalData,
    ECMemberPOD,
    ECMemberPODRelation,
    ECMemberPODRelationType,
    ECMemberState,
    ECPodCreationType,
    ECPodUtilityType,
    ECSubjectType,
    EnergyCommunity,
    EnergyCommunityNamedPod,
    ENTITY_METADATA,
    EntityType,
    NewECMemberRequest,
    PlantStatisticalProfile,
    Pod,
    PodStatisticalProfile,
    SpatialCoordinates,
    UpdateECMemberRequest
} from 'smag-commons/cfg/hierarchy';
import {
    AbstractControl,
    FormArray,
    FormBuilder,
    FormControl,
    FormGroup,
    ValidationErrors,
    ValidatorFn,
    Validators
} from '@angular/forms';
import {BehaviorSubject, EMPTY, forkJoin, Observable, of, ReplaySubject, Subject, throwError} from 'rxjs';
import {
    catchError,
    debounceTime,
    filter,
    first,
    map,
    pairwise,
    startWith,
    switchMap,
    takeUntil,
    tap
} from 'rxjs/operators';
import {SmagApiService} from '../../../api/smag-api.service';
import {HierarchyEntity, HierarchyService} from '../../../shared/hierarchy.service';
import {FeaturesConfig, ProducerPodStatisticalProfileRequest} from 'smag-commons/dto/api';
import {productionPODValidator, validateNotBlank, validatePOD} from '../../../commons/validators';
import * as moment from 'moment-timezone';
import {Moment} from 'moment-timezone';
import {ConfigurationService} from '../../../shared/configuration.service';
import {
    ATECO_PATTERN,
    DOCUMENT_TYPE_PATTERN,
    EMAIL_PATTERN,
    IBAN_PATTERN,
    MOBILE_PATTERN,
    NAME_PATTERN,
    TAX_ID_PATTERN,
    VAT_NUMBER_PATTERN
} from '../../../shared/patterns';
import {
    ATECO_MAX_CHARS,
    DEFAULT_MAX_CHARS,
    EMAIL_MAX_CHARS,
    IBAN_MAX_CHARS,
    LAND_REGISTRY_MAX_CHARS,
    MOBILE_MAX_CHARS,
    NAME_MAX_CHARS,
    POD_MAX_CHARS,
    TAX_ID_MAX_CHARS,
    VAT_NUMBER_MAX_CHARS
} from '../../../shared/limits';
import {DialogService} from '../../../shared/dialog/dialog.service';
import {I18NService} from '../../../i18n/i18n.service';
import {AddressExt} from '../../../shared/google-maps-address-autocomplete/google-maps-address-autocomplete.component';
import {ECMemberAddress, ECMemberDocument, EnergyCommunityNamedPlant} from 'smag-commons/cfg/hierarchy/ec';
import {MsgBoxService} from '../../../shared/msg-box.service';
import * as uuid from 'uuid';
import {VoltageLevel} from 'smag-commons/cfg/voltage-level';
import {Phases, RateMode} from 'smag-commons/cfg';
import {ENTITY_NAME_VALIDATION_PATTERN} from '../../../shared/entity-names';
import {
    ResetContext as StatisticalProfileResetContext,
    StatisticalProfilePlantResource,
    StatisticalProfilePodResourceType,
    StatisticalProfileResourceType
} from 'src/app/shared/statistical-profile/statistical-profile.component';
import {StatisticalProfilePodResource} from '../../../shared/statistical-profile/statistical-profile.component';
import {ResetContext as PredictiveModelResetContext} from '../../../shared/predictive-model/predictive-model.component';
import {PredictiveModelInfo} from 'smag-commons/cfg/prediction';
import {WizardPodCardMode} from 'src/app/shared/wizard-pod-card/wizard-pod-card.component';
import {errorCode, getContractualPowerValues, isBlank, isDefined} from '../../../commons/commons';
import {MatHorizontalStepper} from '@angular/material/stepper';
import {SpinnerData} from '../../../shared/spinner/spinner.component';
import {ErrorCode} from '../../../api/errors';
import * as _ from 'lodash';
import {
    FormError,
    getEntitySubtype,
    getFormControl,
    getIncentiveTypes,
    getNominalPower,
    getPlantType,
    getPodStatisticalProfileRequestPlantType,
    getPodUtilityTypes,
    getStatisticalProfileResourceType,
    hasDate,
    isControlOK,
    mkMemberName,
    mkPredictiveModelPlantResource,
    mkStatisticalProfilePlantResource,
    mkTextField,
    PLANT_TYPES,
    PlantType,
    POD_PHASES,
    POD_TYPES,
    POD_VOLTAGES,
    PodEnergyProfiles,
    PV_PLANT_TYPE,
    WIND_PLANT_TYPE
} from '../../../shared/energy-community';
import {StepperSelectionEvent} from '@angular/cdk/stepper';
import {MatSelectChange} from '@angular/material/select';
import {Member} from '../ec-members.component';
import {
    googleCompatibleTextAddress,
    isProducerEntity,
    ITALY, mkMemberAddress,
    validatePod as validatePodPattern
} from 'smag-commons/fcn';
import {
    CAPITAL_GRANT_MAX,
    CAPITAL_GRANT_MIN,
    limitDecimals,
    UPPER_INCENTIVE_BOUNDARY
} from '../../../shared/ec-economics';
import {
    ECMemberRole,
    getMemberRole,
    getPodRole,
    MAX_FILE_MB,
    mimeTypesNames,
    relationsToRoleConverter,
    roleToRelationConverter,
    validateDocumentType,
    validateFile
} from '../../../shared/ec-members';
import {getRateMode} from '../../../shared/statistical-profiles';
import {GenericPipe} from '../../../shared/shared.pipe';
import {COUNTRIES, Country, LOCALIZATIONS as COUNTRY_LOCALIZATIONS} from 'src/app/shared/countries';
import {MUNICIPALITIES, Municipality} from '../../../shared/municipalities';
import {HttpErrorResponse} from '@angular/common/http';

interface EditedDocument {
    documentId?: string; // in case we are overwriting an already existing document
    documentType: string;
    metadata?: object; // used for some types of documents
    expiration?: number;
    file?: File; // uploaded file
}

const JURIDICAL_TYPES: ECJuridicalType[] = [
    ECJuridicalType.INDIVIDUAL,
    ECJuridicalType.CORPORATE_ENTITY
];

const LEGAL_FORM_TYPES: ECLegalForm[] = [
    ECLegalForm.ENTREPRENEUR_SOLE_PROPRIETORSHIP_AGRICULTURAL_ENTERPRISE,
    ECLegalForm.OTHER_INDIVIDUALS
];

const SUBJECT_TYPES: ECSubjectType[] = [
    ECSubjectType.TERRITORIAL_BODY_LOCAL_AUTHORITY_OTHER_BODY,
    ECSubjectType.PMI,
    ECSubjectType.ASSOCIATION_WITH_LEGAL_PERSONALITY_UNDER_PRIVATE_LAW,
    ECSubjectType.ASSOCIATED_FIRM_COMPANY_OF_PROFESSIONALS,
    ECSubjectType.OTHER,
    // ECSubjectType.COMMUNITY  // unnecessary with members
];

const ENTITY_SUBTYPES: ECEntitySubtype[] = [
    ECEntitySubtype.STATE,
    ECEntitySubtype.REGION,
    ECEntitySubtype.PROVINCE,
    ECEntitySubtype.MUNICIPALITY,
    ECEntitySubtype.MUNICIPALITIES_UNION,
    ECEntitySubtype.MOUNTAIN_COMMUNITY,
    ECEntitySubtype.ISLAND_COMMUNITY,
    ECEntitySubtype.METROPOLITAN_CITY,
    ECEntitySubtype.RESEARCH_AND_TRAINING_ORGANIZATION,
    ECEntitySubtype.RELIGIOUS_ORGANIZATION,
    ECEntitySubtype.THIRD_SECTOR_ORGANIZATION,
    ECEntitySubtype.ENVIRONMENT_PREVENTION_ORGANIZATION,
    ECEntitySubtype.LOCAL_ADMINISTRATION_ORGANIZATION
];

const ACCESS_TYPES: ECMemberAccessType[] = [
    ECMemberAccessType.MOBILE,
    ECMemberAccessType.ADVANCED,
    ECMemberAccessType.NONE
];

const PARTICIPATION_TYPES: ECMemberParticipationType[] = [
    ECMemberParticipationType.MEMBER,
    ECMemberParticipationType.THIRD_PARTY_PRODUCER
];

interface Document {
    name: string;
    documentId?: string; // only defined if already existing before the current session
    file?: File; // only defined if uploaded during the current session
}

const DEFAULT_JURIDICAL_TYPE = ECJuridicalType.INDIVIDUAL;
const DIALOG_WIDTH = 900;

const ALLOWED_COUNTRIES = [ITALY];

const TYPES_DEBOUNCE_MILLIS = 250;

export interface FormCommunity {
    community: EnergyCommunity;
    communityData: HierarchyEntity;
}

const INTERNAL_DOCUMENT_PREFIX = 'internal_';
export const EC_DOCUMENT_TYPE_OTHER = 'Other';

interface PodInfo {
    namedPod?: EnergyCommunityNamedPod;
    memberInfo?: { memberRole: ECMemberRole; memberName?: string; }[];
    error?: Error;
}

const MIN_SIGNATORY_AGE = 18;

@Component({
    selector: 'app-new-ec-member',
    templateUrl: './new-ec-member.component.html',
    styleUrls: ['./new-ec-member.component.scss']
})
export class NewECMemberComponent implements OnInit, OnDestroy {
    private destroyed = new Subject<void>();
    private timeZone: string = null;
    private currentAddress: ECMemberAddress = null;
    private currentCoordinates: SpatialCoordinates = null;
    private readonly documents: { [id: string]: Document } = {};
    private readonly genericPipe: GenericPipe;
    private readonly countriesByCode: { [code: string]: Country } = {};

    @ViewChild('deletePod') private deletePodDialog: TemplateRef<any>;
    @ViewChild('setEffectiveDate') private setEffectiveDateDialog: TemplateRef<any>;
    @ViewChild('setPlantDate') private setPlantDateDialog: TemplateRef<any>;
    @ViewChild('spinner') private spinnerDialog: TemplateRef<any>;
    @ViewChild('adviceDialog') private adviceDialog: TemplateRef<any>;
    @ViewChild('setDeactivationDate') private setDeactivationDateDialog: TemplateRef<any>;

    @Input() community: FormCommunity;
    @Input() member: Member;
    @Output() closeDialog: EventEmitter<boolean> = new EventEmitter<boolean>();

    featureConfig: FeaturesConfig;
    ecMember: ECMember;
    form1: FormGroup;
    form2: FormGroup;
    step3Enabled: boolean;
    step4Enabled: boolean;
    step3Busy = false;
    window = window;
    documentTypes: ReplaySubject<string[]> = new ReplaySubject<string[]>(1);
    accessTypeFormControl: FormControl = new FormControl(null, []);
    participationTypes: ECMemberParticipationType[] = PARTICIPATION_TYPES;
    accessTypes: ECMemberAccessType[] = ACCESS_TYPES;
    countries: Country[] = [];

    readonly MOBILE_MAX_CHARS = MOBILE_MAX_CHARS;
    readonly EMAIL_MAX_CHARS = EMAIL_MAX_CHARS;
    readonly POD_MAX_CHARS = POD_MAX_CHARS;
    readonly TAX_ID_MAX_CHARS = TAX_ID_MAX_CHARS;
    readonly VAT_NUMBER_MAX_CHARS = VAT_NUMBER_MAX_CHARS;
    readonly IBAN_MAX_CHARS = IBAN_MAX_CHARS;
    readonly ACCEPTED_MIME_TYPES = mimeTypesNames();
    readonly MAX_FILE_MB: number = MAX_FILE_MB;
    readonly DEFAULT_MAX_CHARS = DEFAULT_MAX_CHARS;
    readonly POD_TYPES = POD_TYPES;
    readonly NAME_MAX_CHARS = NAME_MAX_CHARS;
    readonly ATECO_MAX_CHARS = ATECO_MAX_CHARS;
    readonly LAND_REGISTRY_MAX_CHARS = LAND_REGISTRY_MAX_CHARS;
    readonly UPPER_INCENTIVE_BOUNDARY = UPPER_INCENTIVE_BOUNDARY;
    readonly PLANT_TYPES = PLANT_TYPES;
    readonly EC_DOCUMENT_TYPE_OTHER = EC_DOCUMENT_TYPE_OTHER;
    readonly JURIDICAL_TYPES = JURIDICAL_TYPES;
    readonly LEGAL_FORM_TYPES = LEGAL_FORM_TYPES;
    readonly SUBJECT_TYPES = SUBJECT_TYPES;
    readonly ENTITY_SUBTYPES = ENTITY_SUBTYPES;
    readonly POD_PHASES = POD_PHASES;
    readonly POD_VOLTAGES = POD_VOLTAGES;
    readonly WIND_PLANT_TYPE = WIND_PLANT_TYPE;
    readonly MUNICIPALITIES = MUNICIPALITIES;
    readonly Object = Object;
    readonly ECJuridicalType = ECJuridicalType;
    readonly ECSubjectType = ECSubjectType;
    readonly PodEnergyProfiles = PodEnergyProfiles;
    readonly WizardPodCardMode = WizardPodCardMode;
    readonly FormError = FormError;
    readonly contractualPowerValues = getContractualPowerValues();
    readonly getFormControl = getFormControl;
    readonly getPodUtilityTypes = getPodUtilityTypes;
    readonly limitDecimals = limitDecimals;
    readonly getIncentiveTypes = getIncentiveTypes;
    readonly hasDate = hasDate;
    readonly mkPredictiveModelPlantResource = mkPredictiveModelPlantResource;
    readonly mkStatisticalProfilePlantResource = mkStatisticalProfilePlantResource;
    readonly isControlOK = isControlOK;
    readonly isDefined = isDefined;

    constructor(private readonly apiService: SmagApiService,
                private readonly configurationService: ConfigurationService,
                private readonly hierarchyService: HierarchyService,
                private readonly dialogService: DialogService,
                private readonly i18n: I18NService,
                private readonly msgBoxService: MsgBoxService,
                private fb: FormBuilder) {

        const localizedCountries = COUNTRY_LOCALIZATIONS[this.i18n.language];
        this.countries = localizedCountries ?
            COUNTRIES.map(country => ({
                code: country.code,
                name: localizedCountries[country.code] || country.name,
            }) as Country) :
            COUNTRIES;
        this.countriesByCode = {};
        this.countries.forEach(country => this.countriesByCode[country.code] = country);

        this.form1 = this.fb.group({
            participationType: new FormControl(null, [Validators.required]),
            juridicalType: new FormControl(DEFAULT_JURIDICAL_TYPE, [Validators.required]),
            legalForm: new FormControl(null, []),
            subjectType: new FormControl(null, []),
            entitySubtype: new FormControl(null, []),
            firstName: new FormControl(null, [validateNotBlank, Validators.pattern(NAME_PATTERN)]),
            lastName: new FormControl(null, [validateNotBlank, Validators.pattern(NAME_PATTERN)]),
            companyName: new FormControl(null, []),
            email: new FormControl(null, [Validators.pattern(EMAIL_PATTERN)]),
            pec: new FormControl(null, [Validators.pattern(EMAIL_PATTERN)]),
            confirmEmail: new FormControl(null, []),
            mobileNumber: new FormControl(null, [Validators.pattern(MOBILE_PATTERN)]),
            phoneNumber: new FormControl(null, [Validators.pattern(MOBILE_PATTERN)]),
            fiscalCode: new FormControl(null, [Validators.pattern(TAX_ID_PATTERN)]),
            signatoryTaxCode: new FormControl(null, [Validators.pattern(TAX_ID_PATTERN)]),
            vatNumber: new FormControl(null, [Validators.pattern(VAT_NUMBER_PATTERN)]),
            iban: new FormControl(null, [Validators.pattern(IBAN_PATTERN)]),
            birthDate: new FormControl(null, []),
            birthCountry: new FormControl(null, []),
            birthMunicipality: new FormControl(null, []),
            atecoCode: new FormControl(null, [Validators.pattern(ATECO_PATTERN)]),
            address: new FormControl(null, [Validators.required]),
            documents: this.fb.array([]),
            // shown in the third wizard step
            sendInvitation: new FormControl(false, [Validators.required])
        }, {
            validators: [validateForm]
        });
        this.form2 = new FormGroup({pods: new FormArray([])});
        this.genericPipe = new GenericPipe(i18n);
    }

    ngOnInit() {
        this.ecMember = this.member?.member;

        this.configurationService.timeConfig
            .pipe(takeUntil(this.destroyed))
            .subscribe(timeCfg => this.timeZone = timeCfg.time_zone);

        this.apiService.getFeatureConfig()
            .pipe(takeUntil(this.destroyed))
            .subscribe(featureConfig => {
                this.featureConfig = featureConfig;
                this.participationTypes = this.featureConfig.third_party_producer ? PARTICIPATION_TYPES : [ECMemberParticipationType.MEMBER];
                const participationType = this.ecMember && this.ecMember.participation_type;
                if (this.member && this.member.member.participation_type !== ECMemberParticipationType.THIRD_PARTY_PRODUCER) {
                    forkJoin(this.member.pods.map(pod => this.apiService.findEntityByPOD(pod.pod.pod)
                        .pipe(switchMap(resp => this.apiService.getECPod(this.community.communityData.id, resp.id)),
                            map(ecNamedPod => isProducerEntity(pod.resource.entity, ecNamedPod.pod)))))
                        .subscribe(isProducer =>
                            this.setAccessTypes(isProducer.some(Boolean) ? ECMemberParticipationType.THIRD_PARTY_PRODUCER : null));
                } else {
                    this.setAccessTypes(participationType);
                }
            });
        this.apiService.getCustomerConfig(this.community.communityData.id).subscribe(customerCfg =>
            this.documentTypes.next([EC_DOCUMENT_TYPE_OTHER].concat(customerCfg.subject_documents?.map(document => document.name) || [])));
        this.internalInit();
    }

    ngOnDestroy() {
        this.destroyed.next();
        this.destroyed.complete();
    }

    get isStep2Valid(): boolean {
        let valid = true;
        this.podControls.forEach(podForm => {
            if (valid) {
                const existingResource = this.hasExistingResource(podForm);
                let checkProducerPods = true;
                // Checks only if there is a POD with no subject assigned
                if (this.podExists(podForm)) {
                    // Mobile app members cannot have producer pods
                    if (this.isProducerResource(podForm) && this.isMobileMember) {
                        checkProducerPods = false;
                    }
                    // Third party producer cannot have consumer pods
                    if (this.isThirdPartyProducer && this.isConsumerResource(podForm)) {
                        checkProducerPods = false;
                    }
                    // Third party producer cannot have prosumer pods that are not seu, or it cannot have prosumer pods if seu is disabled
                    if (this.isThirdPartyProducer && this.isProsumerResource(podForm) && (!this.isSeu(podForm) || !this.featureConfig.seu)) {
                        checkProducerPods = false;
                    }
                    // Third party producer cannot have producer pods that are seu
                    if (this.isThirdPartyProducer && this.isProducerResource(podForm) && this.isSeu(podForm)) {
                        checkProducerPods = false;
                    }
                }
                // .dirty is not needed to validate step
                const podFormValid = podForm.controls.podCode.valid &&
                    (podForm.controls.podAddressLikeMember.value ||
                        (!isBlank(podForm.controls.podAddress.value) && podForm.controls.podAddress.valid));
                valid = valid && podFormValid && !existingResource && checkProducerPods;
            }
        });
        return valid && !this.continueDisabled;
    }

    get isCommunityActivationDateEmpty(): boolean {
        return !this.community.community.activation_date;
    }

    get isCompany(): boolean {
        return !(this.juridicalType === ECJuridicalType.INDIVIDUAL && this.legalFormType === ECLegalForm.OTHER_INDIVIDUALS);
    }

    get juridicalType(): ECJuridicalType {
        return this.form1.controls.juridicalType.value;
    }

    get legalFormType(): ECLegalForm {
        return this.form1.controls.legalForm.value;
    }

    get entitySubtype(): ECEntitySubtype {
        return this.form1.controls.entitySubtype.value;
    }

    get subjectType(): ECSubjectType {
        return this.form1.controls.subjectType.value;
    }

    get participationType(): ECMemberParticipationType {
        return this.form1.controls.participationType.value;
    }

    get isMember() {
        return this.participationType === ECMemberParticipationType.MEMBER;
    }

    get isThirdPartyProducer() {
        return this.participationType === ECMemberParticipationType.THIRD_PARTY_PRODUCER;
    }

    get hasSEU(): boolean {
        const podControls = this.form2.controls.pods.value as any[];
        return podControls.some(c => c.seu);
    }

    get isMobileMember() {
        return this.isMember && this.getAccessType === ECMemberAccessType.MOBILE;
    }

    get isUpdate(): boolean {
        return !!this.member;
    }

    get invalidCompanyName(): boolean {
        return this.form1.controls.companyName.dirty && this.form1.controls.companyName.invalid;
    }

    get invalidFirstName(): boolean {
        return this.form1.controls.firstName.dirty && this.form1.controls.firstName.invalid;
    }

    get invalidLastName(): boolean {
        return this.form1.controls.lastName.dirty && this.form1.controls.lastName.invalid;
    }

    get invalidEmail(): boolean {
        return this.form1.controls.email.dirty && this.form1.controls.email.invalid;
    }

    get uniqueEmailError(): boolean {
        return this.form1.controls.email.hasError('unique_email');
    }

    get invalidConfirmEmail(): boolean {
        return this.form1.controls.confirmEmail.dirty && this.form1.controls.confirmEmail.invalid;
    }

    get invalidMobileNumber(): boolean {
        return this.form1.controls.mobileNumber.dirty && this.form1.controls.mobileNumber.invalid;
    }

    get notMatchingEmails(): boolean {
        return this.form1.controls.confirmEmail.dirty && this.form1.controls.confirmEmail.hasError(FormError.NOT_MATCHING_EMAILS);
    }

    get communityActivationDate(): Moment {
        return this.community && this.community.community ? moment.tz(this.community.community.activation_date, this.timeZone) : undefined;
    }

    get addressFormControl(): FormControl {
        return this.form1.controls.address as FormControl;
    }

    get addressLabel(): string {
        return this.juridicalType === ECJuridicalType.CORPORATE_ENTITY ||
        this.juridicalType === ECJuridicalType.INDIVIDUAL && this.legalFormType === ECLegalForm.ENTREPRENEUR_SOLE_PROPRIETORSHIP_AGRICULTURAL_ENTERPRISE ?
            'Registered office address' : 'Residential address';
    }

    get birthDateLabel(): string {
        return this.juridicalType === ECJuridicalType.INDIVIDUAL ? 'Birth date' : 'Signatory\'s birth date';
    }

    get birthCountryLabel(): string {
        return this.juridicalType === ECJuridicalType.INDIVIDUAL ? 'Birth country' : 'Signatory\'s birth country';
    }

    get birthMunicipalityLabel(): string {
        return this.juridicalType === ECJuridicalType.INDIVIDUAL ? 'Birth municipality' : 'Signatory\'s birth municipality';
    }

    get getAccessType(): ECMemberAccessType {
        return this.accessTypeFormControl.value || this.ecMember?.access_type;
    }

    get firstName(): string {
        return this.form1.controls.firstName.value;
    }

    get firstNameLabel(): string {
        return this.juridicalType === ECJuridicalType.CORPORATE_ENTITY ? 'Signatory\'s first name' : 'First name';
    }

    get companyName(): string {
        return this.form1.controls.companyName.value;
    }

    get lastName(): string {
        return this.form1.controls.lastName.value;
    }

    get lastNameLabel(): string {
        return this.juridicalType === ECJuridicalType.CORPORATE_ENTITY ? 'Signatory\'s last name' : 'Last name';
    }

    get continueDisabled(): boolean {
        return this.documentControls.some(fg => this.uploadedFileName(fg) === null);
    }

    get showInvitingForm(): boolean {
        return !this.isUpdate ||
            this.ecMember.state === ECMemberState.NOT_INVITED ||
            this.ecMember.state === ECMemberState.PENDING ||
            this.ecMember.state === ECMemberState.CANCELED;
    }

    get enableInvitationInput(): boolean {
        const email = this.form1.controls.email;
        const confirmEmail = this.form1.controls.confirmEmail;
        const mobile = this.form1.controls.mobileNumber;
        const sendInvitation = this.form1.controls.sendInvitation;
        if (email.value && confirmEmail.value && mobile.value && email.valid && confirmEmail.valid && mobile.valid) {
            return true;
        }
        sendInvitation.setValue(false);
        return false;
    }

    get documentsFormArray(): FormArray {
        return this.form1.controls.documents as FormArray;
    }

    get documentControls(): FormGroup[] {
        return (this.documentsFormArray).controls as FormGroup[];
    }

    get podsFormArray(): FormArray {
        return this.form2.controls.pods as FormArray;
    }

    get podControls(): FormGroup[] {
        return (this.podsFormArray).controls as FormGroup[];
    }

    get isNotInvited(): boolean {
        if (this.isUpdate) {
            return this.ecMember.state === ECMemberState.NOT_INVITED || this.ecMember.state === ECMemberState.CANCELED;
        }
        return false;
    }

    get isPending() {
        if (this.isUpdate) {
            return this.ecMember.state === ECMemberState.PENDING;
        }
        return false;
    }

    get duplicatePodValidator(): ValidatorFn {
        const scope = this;
        return (currentCtrl: AbstractControl): ValidationErrors => {
            const currentValue = currentCtrl.value;

            if (typeof currentValue !== 'string' || isBlank(currentValue)) { // allow optional control
                return null;
            }

            let error = null;
            for (const fg of scope.podControls) {
                const ctrl = fg.controls.podCode;
                if (ctrl !== currentCtrl) {
                    const ctrlValue = ctrl.value;
                    if ((typeof ctrlValue === 'string') && !isBlank(ctrlValue)) {
                        if (currentValue === ctrlValue) {
                            error = {[FormError.UNIQUE_POD]: true} as ValidationErrors;
                        }
                    }
                }
            }
            return error;
        };
    }

    get bornInItaly(): boolean {
        // look for an already set country; if none, fall back on currently edited value
        let birthCountryCode = this.ecMember?.personal_data.birth_country;
        if (!birthCountryCode) {
            const editedBirthCountry: Country = this.form1?.controls.birthCountry?.value;
            if (editedBirthCountry) {
                birthCountryCode = editedBirthCountry.code;
            }
        }
        return birthCountryCode === 'IT';
    }

    get taxIdMaxChars(): number {
        return this.juridicalType === ECJuridicalType.INDIVIDUAL ? TAX_ID_MAX_CHARS : VAT_NUMBER_MAX_CHARS;
    }

    get maxBirthDate(): Moment {
        return moment().startOf('day').subtract(MIN_SIGNATORY_AGE, 'years');
    }

    getCountryName(country: Country): string {
        return country?.name;
    }

    getMunicipalityName(municipality: Municipality): string {
        return municipality?.name;
    }

    onChangeBirthCountry() {
        const birthMunicipalityCtrl = this.form1.controls.birthMunicipality;
        if (birthMunicipalityCtrl) {
            birthMunicipalityCtrl.setValue(null);
            birthMunicipalityCtrl.markAsPristine();
        }
    }

    getSeuTooltip(podForm: FormGroup) {
        if (this.isProsumerResource(podForm)) {
            return this.i18n.translate('This POD has a Prosumer profile, therefore it can only use a plant in SEU mode.');
        }
        if (this.isProducerResource(podForm) || this.isConsumerResource(podForm)) {
            return this.i18n.translate('SEU mode is not available for this POD.');
        }
        return this.i18n.translate('Select this option if the POD provides a plant for self-consumption by another subject (Utility Efficiency System).');
    }

    isPersistentPod(podForm: FormGroup): boolean {
        return !!podForm.controls.persistentPod.value as boolean;
    }

    podExists(podForm: FormGroup): boolean {
        return podForm.controls.podExists.value as boolean;
    }

    podMessage(podForm: FormGroup): string {
        if (this.isProducerResource(podForm) && this.isMobileMember) {
            return this.i18n.translate('Enter a new POD or consumer/prosumer POD');
        }
        // third party producer can't have a consumer resource or, if seu is disabled, a prosumer resource
        if (this.isThirdPartyProducer && (this.isConsumerResource(podForm) || this.isProsumerResource(podForm) && !this.featureConfig.seu)) {
            return this.i18n.translate('Enter a new POD or producer POD');
        }
        if (!!this.podHasOwnerAndHolder(podForm)) {
            return this.i18n.translate('You cannot add this POD since it already has both owner and holder assigned.');
        }
        return podForm.controls.podMessage.value as string;
    }

    hasEntity(podForm: FormGroup): boolean {
        return !!podForm.controls.entityName.value as boolean;
    }

    podError(fg: FormGroup): string {
        let podErr = null;
        if (fg.controls.podCode.dirty && fg.controls.podCode.hasError(FormError.UNIQUE_POD) &&
            !this.podHasOwnerAndHolder(fg)) {
            // pod already exists, do not need to be exposed if the pod has an owner or holder
            podErr = FormError.UNIQUE_POD;
        }
        if (fg.controls.podCode.dirty && fg.controls.podCode.hasError(FormError.POD_HAS_CAPITAL_GRANT)) {
            podErr = FormError.POD_HAS_CAPITAL_GRANT;
        }
        if (fg.controls.podCode.dirty && (fg.controls.podCode.hasError(FormError.POD) || fg.controls.podCode.hasError(FormError.EMPTY))) {
            podErr = FormError.POD;
        }
        let dateErr = null;
        if (fg.controls.podActivationDate.dirty && fg.controls.podActivationDate.invalid) {
            dateErr = FormError.BAD_DATE;
        }
        return podErr || dateErr;
    }

    podHasOwnerAndHolder(fg: FormGroup) {
        if (fg.controls.podCode.dirty && fg.controls.podCode.hasError(FormError.POD_HAS_OWNER_OR_HOLDER)) {
            return FormError.POD_HAS_OWNER_OR_HOLDER;
        }
        return null;
    }

    getPodErrorMessage(podForm: FormGroup): string {
        if (this.podError(podForm) === FormError.POD) {
            return this.i18n.translate('Invalid POD');
        }
        if (this.podError(podForm) === FormError.UNIQUE_POD || this.hasExistingResource(podForm)) {
            return this.i18n.translate('POD already existing');
        }
        if (this.isProducerResource(podForm) && this.isThirdPartyProducer && this.podError(podForm) === FormError.POD_HAS_CAPITAL_GRANT) {
            return this.i18n.translate('Enter a new POD or producer POD without a configured capital grant');
        }
    }

    plantPodError(fg: FormGroup): string {
        let podErr = null;
        if (fg.controls.podCode.dirty && fg.controls.podCode.hasError(FormError.UNIQUE_POD)) {
            podErr = FormError.UNIQUE_POD;
        }
        if (fg.controls.podCode.dirty && (fg.controls.podCode.hasError(FormError.POD) || fg.controls.podCode.hasError(FormError.EMPTY))) {
            podErr = FormError.POD;
        }
        return podErr;
    }

    getControl(form: FormGroup, name: string): FormControl {
        return form.controls[name] as FormControl;
    }

    addressChange(event: AddressExt | null) {
        this.currentAddress = mkMemberAddress(event);
        this.currentCoordinates = event ? event.coordinates : null;
        this.addressFormControl.setValue(this.currentAddress);
    }

    onClickAddDocument() {
        // const id = Math.max(this.documentControls.map(fg => fg.controls.id.value as number)) + 1;
        const formUuid = INTERNAL_DOCUMENT_PREFIX + uuid.v4(); // internal document id
        const documentForm = this.fb.group({
            type: new FormControl('', [Validators.required]),
            customType: new FormControl('', [Validators.pattern(DOCUMENT_TYPE_PATTERN)]),
            id: new FormControl(formUuid, null),
            fileName: new FormControl(null, null)
        }, {
            validators: [validateDocumentType]
        });
        this.documentsFormArray.push(documentForm);
    }

    uploadedFileName(fc: FormGroup) {
        return fc.controls.fileName.value;
    }

    onUploadDocumentFile(documentForm: FormGroup, event: Event) {
        const files = 'files';
        const id = documentForm.controls.id.value as string;
        if (event.target[files] && event.target[files].length) {
            const file: File = event.target[files][0];
            if (validateFile(file, this.msgBoxService, this.i18n)) {
                documentForm.controls.fileName.setValue(file.name);
                this.documents[id] = {
                    name: file.name,
                    documentId: id,
                    file
                };
            }
        } else {
            documentForm.controls.fileName.setValue(null);
        }
        this.updateForm(documentForm);
    }

    hasDocFileError(fg: FormGroup): boolean {
        return fg.hasError('missing_file');
    }

    onClickRemoveDocument(index: number, documentForm: FormGroup) {
        const id = documentForm.controls.id.value;
        this.documents[id] = null;
        this.documentsFormArray.removeAt(index);
    }

    cancel() {
        this.closeDialog.emit(false);
    }

    detectSelectChanges($event: MatSelectChange) {
        if (!this.isUpdate) {
            const value = $event.value as ECMemberParticipationType;
            this.setAccessTypes(value);
        }
    }

    /**
     * Manage selection stepper changes from header
     */
    selectionChange(selectionEvent: StepperSelectionEvent) {
        if (selectionEvent.selectedIndex === 2) {
            // like  gotoStep3
            this.changeEnergyProfile();
            this.mkPromises().then();
            this.disableUtilityType();
        }
    }

    canResetActivationDate(podForm: FormGroup) {
        const currentPod = podForm.controls.podCode.value;
        const pod = this.ecMember.pods.find((ecMemberPod) => ecMemberPod.pod === currentPod);
        if (pod) {
            return !pod.effective_date;
        }
        return true;
    }

    podAddressFormControl(fg: FormGroup): FormControl {
        return fg.controls.podAddress as FormControl;
    }

    onClickAddPod() {
        const podForm = this.fb.group({
            podCode: new FormControl(null, [validateNotBlank, validatePOD, this.duplicatePodValidator]),
            podMessage: new FormControl(null, []),
            podAddressLikeMember: new FormControl({value: false, disabled: this.foreignCountry()}, []),
            podAddress: new FormControl('', [Validators.required]),
            aspiringProsumer: new FormControl(null, []),
            // for internal use only
            internalPodAddress: new FormControl(null, []),
            // for internal use only
            internalPodCoordinates: new FormControl(null, []),
            hasWifi: new FormControl(false, []),
            seu: new FormControl(false, []),
            podMemberRole: new FormControl({value: ECMemberRole.HOLDER, disabled: true}, [Validators.required]),
            availablePodMemberRoles: new FormControl(this.getDefaultMemberRoles(), []),
            podProvider: new FormControl('', []),
            podActivationDate: new FormControl('', []),
            podDeactivationDate: new FormControl('', []),
            creationType: new FormControl(ECPodCreationType.MANUAL, []),
            landRegistrySection: new FormControl(null, []),
            landRegistrySheet: new FormControl(null, []),
            landRegistryParticle: new FormControl(null, []),
            landRegistrySubdivision: new FormControl(null, []),
            // third step
            podEnergyProfile: new FormControl(this.getDefaultEnergyProfile(), [Validators.required]),
            podUtilityType: new FormControl('', [Validators.required]),
            podVoltage: new FormControl(VoltageLevel.LOW, [Validators.required]),
            podPhase: new FormControl(Phases.SINGLE, [Validators.required]),
            contractualPower: new FormControl('', []),

            plants: new FormArray([]),
            // NB: if entityName is filled, energy parameters are not editable
            entityName: new FormControl(null, []),
            podName: new FormControl(null, []),
            podEntityEnergyProfile: new FormControl(null, []),
            podExists: new FormControl(null, []),
            persistentPod: new FormControl(false, []),
            predictiveModel: new FormControl(null, []),

            // fourth step
            profile: new FormControl(null, []) // statistical profile
        });

        podForm.controls.podEnergyProfile.valueChanges
            .pipe(takeUntil(this.destroyed))
            .subscribe(value => {
                if (!this.podExists(podForm)) {
                    if (value === PodEnergyProfiles.PRODUCER || value === PodEnergyProfiles.PROSUMER) {
                        if (this.plantsFormArray(podForm).length === 0) {
                            this.onClickAddPlant(podForm);
                        }
                    } else {
                        this.plantsFormArray(podForm).clear();
                    }
                    if (value === PodEnergyProfiles.PRODUCER) {
                        podForm.controls.podUtilityType.setValue('');
                        podForm.controls.podUtilityType.disable();
                    } else {
                        podForm.controls.podUtilityType.enable();
                        podForm.controls.podUtilityType.setValidators(Validators.required);
                    }
                    this.setAccessTypesByEnergyProfiles();
                }
            });


        this.listenForPodCodeChanges(podForm);

        podForm.controls.podEnergyProfile.valueChanges
            .pipe(takeUntil(this.destroyed))
            .pipe(startWith(undefined as string), pairwise())
            .subscribe((values: string[]) => {
                if (values[0] !== values[1]) {
                    this.resetPodStatisticalProfile(podForm);
                }
            });
        this.managePodAddressLikeMemberChange(podForm);
        this.podsFormArray.push(podForm);
        // todo evaluate to use value change in podsFormArray
        this.refreshSteps3Enabled();
        this.accessTypeFormControl.reset();
        this.setAccessTypesByEnergyProfiles();

        podForm.controls.podCode.statusChanges.subscribe(() => {
            if (podForm.controls.podCode.valid) {
                podForm.controls.podMemberRole.enable();
            } else {
                podForm.controls.podMemberRole.disable();
            }
        });
    }

    private getDefaultEnergyProfile() {
        if (this.isThirdPartyProducer && this.hasSEU) {
            return PodEnergyProfiles.PROSUMER;
        }
        if (this.isThirdPartyProducer) {
            return PodEnergyProfiles.PRODUCER;
        }
        return PodEnergyProfiles.CONSUMER;
    }

    hasExistingResource(podForm: FormGroup) {
        return !this.podExists(podForm) && podForm.controls.podExists.value;
    }

    isProducerResource(podForm: FormGroup) {
        return podForm.controls.podEntityEnergyProfile.value as PodEnergyProfiles === PodEnergyProfiles.PRODUCER;
    }

    isProsumerResource(podForm: FormGroup) {
        return podForm.controls.podEntityEnergyProfile.value as PodEnergyProfiles === PodEnergyProfiles.PROSUMER;
    }

    isConsumerResource(podForm: FormGroup) {
        return podForm.controls.podEntityEnergyProfile.value as PodEnergyProfiles === PodEnergyProfiles.CONSUMER;
    }

    isSeu(podForm: FormGroup) {
        return podForm.controls.seu.value === true;
    }

    onClickRemovePod(index: number) {
        const podForm = this.podsFormArray.at(index) as FormGroup;
        if (this.isPersistentPod(podForm)) {
            this.dialogService.open<{ pod: string, memberName: string }, boolean>({
                title: this.i18n.translate('Are you sure you want to delete this POD?'),
                template: this.deletePodDialog,
                context: {
                    pod: podForm.controls.podCode.value,
                    memberName: mkMemberName({
                        personal_data: {
                            first_name: this.form1.controls.firstName.value,
                            last_name: this.form1.controls.lastName.value,
                            company_name: this.form1.controls.companyName.value
                        }
                    })
                }
            }).closed.then(del => {
                if (del) {
                    this.podsFormArray.removeAt(index);
                }
            });
        } else {
            this.podsFormArray.removeAt(index);
        }
        this.refreshSteps3Enabled();
    }

    addressPodChange(event: AddressExt, fg: FormGroup) {
        fg.controls.internalPodAddress.setValue(mkMemberAddress(event));
        fg.controls.internalPodCoordinates.setValue(event.coordinates);
    }

    podHasActivationDate(fg: FormGroup): boolean {
        return !!fg.controls.podActivationDate.value;
    }

    onClickResetPodActivationDate(fg: FormGroup) {
        fg.controls.podActivationDate.setValue(null);
    }

    onClickSetPodActivationDate(fg: FormGroup) {
        this.dialogService.open<any, Moment>({
            title: this.i18n.translate('POD activation date'),
            template: this.setEffectiveDateDialog,
            context: {
                minDate: this.communityActivationDate,
                timezone: this.timeZone
            }
        }, {
            width: DIALOG_WIDTH,
        }).closed.then(effectiveDate => {
            if (effectiveDate) {
                fg.controls.podActivationDate.setValue(effectiveDate.startOf('day'));
                fg.controls.podActivationDate.disable();
            }
        });
    }

    onClickSetDeactivationDate(podForm: FormGroup) {
        const minDate = this.podMinDeactivationDate(podForm);
        const today = moment.tz(this.timeZone).startOf('day');
        this.dialogService.open<any, Moment>({
            title: this.i18n.translate('POD deactivation date'),
            template: this.setDeactivationDateDialog,
            context: {
                pod: podForm.controls.podDeactivationDate.value,
                memberName: mkMemberName(this.ecMember),
                minDate,
                date: new FormControl(minDate.isSameOrAfter(today) ?
                    minDate : today, [Validators.required]) // use "now" as a start
            }
        }).closed.then(deactivationDate => {
            if (deactivationDate) {
                podForm.controls.podDeactivationDate.setValue(deactivationDate);
                podForm.controls.podDeactivationDate.disable();
            }
        });
    }

    podMinDeactivationDate(podForm: FormGroup): Moment {
        const effectiveDate: Moment = podForm.controls.podActivationDate.value;
        return effectiveDate ? effectiveDate.clone().add(1, 'days') : this.communityActivationDate.clone().add(1, 'days');
    }

    podHasDeactivationDate(fg: FormGroup): boolean {
        return !!fg.controls.podDeactivationDate.value;
    }

    podDeactivationDateHasError(podForm: FormGroup): boolean {
        const ctrl = podForm.controls.podDeactivationDate;
        return ctrl.invalid;
    }

    isDeactivationDateSetInStone(podForm: FormGroup) {
        const currentPod = podForm.controls.podCode.value;
        const pod = this.ecMember.pods.find((ecMemberPod) => ecMemberPod.pod === currentPod);
        if (pod) {
            return !!pod.deactivation_date;
        }
        return false;
    }

    onClickResetPodDeactivationDate(fg: FormGroup) {
        fg.controls.podDeactivationDate.setValue(null);
    }

    invalidPodProvider(podForm: FormGroup) {
        return podForm.controls.podProvider.dirty && podForm.controls.podProvider.invalid;

    }

    podEnergyProfile(podForm: FormGroup): PodEnergyProfiles {
        return podForm.controls.podEnergyProfile.value as PodEnergyProfiles;
    }

    mkStatisticalProfilePodResource(podForm: FormGroup): StatisticalProfilePodResource {
        const podCode = podForm.controls.podCode.value;
        const plants: StatisticalProfilePlantResource[] = this.plantControls(podForm).map(plantForm => ({
            type: getStatisticalProfileResourceType(plantForm.controls.plantType.value),
            name: plantForm.controls.plantName.value as string,
            profile: plantForm.controls.profile.value as PlantStatisticalProfile,
            nominalPower: plantForm.controls.nominalPower.value
        }));
        return {
            type: StatisticalProfileResourceType.POD,
            name: podCode,
            podType: mkStatisticalProfilePodType(this.podEnergyProfile(podForm)),
            plants
        };
    }

    mkPodStatisticalProfileResetValueProvider(): (ctx: StatisticalProfileResetContext) => Promise<PodStatisticalProfile> {
        return () => Promise.resolve(null); // reset statistical profile for POD (consumer or prosumer)
    }

    plantControls(podForm: FormGroup): FormGroup[] {
        return this.plantsFormArray(podForm).controls as FormGroup[];
    }

    onClickAddPlant(podForm: FormGroup) {
        const defaultType = PV_PLANT_TYPE;
        const plantCounter = this.plantsFormArray(podForm).length + 1;
        const plantForm = this.fb.group({
            plantName: new FormControl(this.mkPlantName(defaultType, plantCounter), [validateNotBlank, Validators.pattern(ENTITY_NAME_VALIDATION_PATTERN)]),
            plantType: new FormControl(defaultType, [Validators.required]),
            nominalPower: new FormControl(null, [Validators.required, Validators.min(0)]),
            capitalGrant: new FormControl(0, this.isThirdPartyProducer ? [] : [Validators.required, Validators.min(CAPITAL_GRANT_MIN), Validators.max(CAPITAL_GRANT_MAX)]),
            enterData: new FormControl(null, []),
            incentiveType: new FormControl(null, [Validators.required]),
            profile: new FormControl(null, [Validators.required]),
            predictiveModel: new FormControl(null, []),
            podCode: new FormControl(null, [this.mkPlantPodValidator()], [])
        });

        plantForm.controls.plantType.valueChanges
            .pipe(takeUntil(this.destroyed))
            .subscribe(value => plantForm.controls.plantName.setValue(this.mkPlantName(value, plantCounter)));

        plantForm.controls.nominalPower.valueChanges
            .pipe(takeUntil(this.destroyed),
                startWith(undefined as number),
                pairwise())
            .subscribe((values: number[]) => {
                if (values[0] !== values[1]) {
                    this.resetPodStatisticalProfile(podForm);
                }
            });

        plantForm.controls.enterData.valueChanges
            .pipe(takeUntil(this.destroyed))
            .subscribe(plantActivationDate => {
                if (plantActivationDate && plantActivationDate.isBefore(moment.tz(UPPER_INCENTIVE_BOUNDARY, this.timeZone))) {
                    plantForm.controls.capitalGrant.setValue(0);
                    plantForm.controls.capitalGrant.disable();
                } else {
                    plantForm.controls.capitalGrant.enable();
                }
                getIncentiveTypes(plantForm, true);  // updates types of incentive based on data entered
            });

        this.updatePlantStatAndPredModOnChange(plantForm);
        this.resetPodStatisticalProfile(podForm);
        this.plantsFormArray(podForm).push(plantForm);
    }

    isPlantControlOK(fg: FormGroup, controlName: string) {
        return fg.controls[controlName].dirty && !fg.controls[controlName].valid;
    }

    getPlantDateError(form: FormGroup): string {
        let dateErr = null;
        if (form.controls.enterData.dirty && form.controls.enterData.invalid) {
            dateErr = FormError.BAD_DATE;
        }
        return dateErr;
    }

    onClickResetPlantDate(plantForm: FormGroup) {
        plantForm.controls.enterData.setValue(null);
    }

    onClickSetPlantDate(plantForm: FormGroup) {
        this.dialogService.open<any, Moment>({
            title: this.i18n.translate('Date of commissioning of the plant'),
            template: this.setPlantDateDialog,
            context: {
                timezone: this.timeZone
            }
        }, {
            width: 900
        }).closed.then(effectiveDate => {
            if (effectiveDate) {
                plantForm.controls.enterData.setValue(effectiveDate.startOf('day'));
                plantForm.controls.enterData.disable();
            }
        });
    }

    mkPlantStatisticalProfileResetValueProvider(plantForm: FormGroup): (ctx: StatisticalProfileResetContext) => Promise<PlantStatisticalProfile> {
        return ctx => {
            const nominalPower = plantForm.controls.nominalPower.value;
            return this.mkPlantDefaultStatisticalProfile(nominalPower, ctx.rateMode || RateMode.SINGLE, getPlantType(plantForm)).toPromise();
        };
    }

    mkPlantPredictiveModelResetValueProvider(plantForm: FormGroup): (ctx: PredictiveModelResetContext) => Promise<PredictiveModelInfo> {
        return () => this.mkPlantDefaultPredictiveModel(getNominalPower(plantForm), getPlantType(plantForm)).toPromise();
    }

    onClickRemovePlant(podForm: FormGroup, j: number) {
        this.plantsFormArray(podForm).removeAt(j);
        this.plantControls(podForm).forEach(fg => {
            const nameSplit = fg.controls.plantName.value.split(' ');
            const currentNumberPlant = isDefined(nameSplit[nameSplit.length - 1]) ? +nameSplit[nameSplit.length - 1] : 0;
            const newNumberPlant = currentNumberPlant < (j + 1) ? currentNumberPlant : currentNumberPlant - 1;
            fg.controls.plantName.setValue(this.mkPlantName(fg.controls.plantType.value, newNumberPlant));
        });
        this.resetPodStatisticalProfile(podForm);
    }

    aspiringProsumerForForm(podForm: FormGroup) {
        return podForm.controls.aspiringProsumer.value;
    }

    removeAspiringProsumer(podForm: FormGroup) {
        podForm.controls.aspiringProsumer.setValue(false);
    }

    goToStep3(stepper: MatHorizontalStepper) {
        stepper.next();
    }

    goToStep4(stepper: MatHorizontalStepper) {
        stepper.next();
    }

    save() {
        if (this.isUpdate) {
            this.updateMember().then();
        } else {
            this.createMember().then();
        }
    }

    showRoleChangeTooltip(podForm: FormGroup) {
        return this.ecMember?.state === ECMemberState.REGISTERED && podForm.controls.podMemberRole.value !== ECMemberRole.OWNER;
    }

    getRoleChangeTooltip() {
        return this.i18n.translate('You cannot set the role to "Owner", as viewing POD data on the app would be inhibited. To enable the change, you can proceed to delete the account');
    }

    memberIsOwnerOnly() {
        return this.podControls.every(podForm => podForm.controls.podMemberRole.value === ECMemberRole.OWNER);
    }

    private refreshSteps3Enabled() {
        this.step3Enabled = this.podControls.map((podForm: FormGroup) => {
            return !podForm.controls.persistentPod.value as boolean; // true if not persistent
        }).reduce((p, c) => p || c, false);
        this.refreshSteps4Enabled();
    }

    private refreshSteps4Enabled() {
        this.step4Enabled = this.step3Enabled &&
            this.podControls.map(podForm => !podForm.controls.entityName.value as boolean) // true if not persistent
                .reduce((p, c) => p || c, false);
        this.setAccessTypesByEnergyProfiles();
    }

    private internalInit(): void {
        //  *** NEW items ***
        if (!this.isUpdate) {
            this.onClickAddPod(); // add an empty pod
            // manage juridical, subject types, entity subtypes values
            this.form1.controls.juridicalType.valueChanges.pipe(
                debounceTime(TYPES_DEBOUNCE_MILLIS),
                takeUntil(this.destroyed))
                .subscribe(() => {
                    this.form1.controls.legalForm.setValue(null);
                    this.form1.controls.subjectType.setValue(null);
                    this.form1.controls.entitySubtype.setValue(null);
                    this.form1.controls.fiscalCode.setValidators(this.juridicalType === ECJuridicalType.CORPORATE_ENTITY ?
                        [Validators.pattern(VAT_NUMBER_PATTERN)] : [Validators.pattern(TAX_ID_PATTERN)]);
                    this.form1.controls.fiscalCode.setValue(this.form1.controls.fiscalCode.value); // trigger validation
                    this.form1.controls.atecoCode.setValue(null);
                    this.form1.controls.signatoryTaxCode.setValue(null);
                });
            this.form1.controls.subjectType.valueChanges.pipe(
                debounceTime(TYPES_DEBOUNCE_MILLIS),
                takeUntil(this.destroyed))
                .subscribe(() => {
                    this.form1.controls.legalForm.setValue(null);  // legal form has no use if we have a subject type
                    this.form1.controls.entitySubtype.setValue(null);
                });
            this.form1.controls.entitySubtype.valueChanges.pipe(
                debounceTime(TYPES_DEBOUNCE_MILLIS),
                takeUntil(this.destroyed))
                .subscribe(() => {
                    this.podControls.forEach(podForm => {
                        podForm.controls.podUtilityType.setValue(null);
                    });
                });
        } else {
            this.form1.controls.participationType.setValue(this.ecMember.participation_type);
            this.form1.controls.juridicalType.setValue(this.ecMember.juridical_type);
            this.form1.controls.legalForm.setValue(this.ecMember.legal_form);
            this.form1.controls.subjectType.setValue(this.ecMember.subject_type);
            this.form1.controls.entitySubtype.setValue(this.ecMember.entity_subtype);
            this.form1.controls.firstName.setValue(this.ecMember.personal_data.first_name);
            this.form1.controls.lastName.setValue(this.ecMember.personal_data.last_name);
            this.form1.controls.companyName.setValue(this.ecMember.personal_data.company_name);
            this.form1.controls.email.setValue(this.ecMember.personal_data.email);
            this.form1.controls.pec.setValue(this.ecMember.personal_data.pec);
            this.form1.controls.confirmEmail.setValue(this.ecMember.personal_data.email);
            this.form1.controls.mobileNumber.setValue(this.ecMember.personal_data.mobile_number);
            this.form1.controls.phoneNumber.setValue(this.ecMember.personal_data.telephone_number);
            this.form1.controls.fiscalCode.setValidators(this.juridicalType === ECJuridicalType.CORPORATE_ENTITY ?
                [Validators.pattern(VAT_NUMBER_PATTERN)] : [Validators.pattern(TAX_ID_PATTERN)]);
            this.form1.controls.fiscalCode.setValue(this.ecMember.personal_data.cf);
            this.form1.controls.signatoryTaxCode.setValue(this.ecMember.signatory_tax_code);
            this.form1.controls.vatNumber.setValue(this.ecMember.personal_data.piva);
            this.form1.controls.iban.setValue(this.ecMember.personal_data.iban);
            this.form1.controls.birthDate.setValue(this.ecMember.personal_data.birth_date ? moment(this.ecMember.personal_data.birth_date).startOf('day') : null);
            this.form1.controls.birthCountry.setValue(this.ecMember.personal_data.birth_country ? this.countriesByCode[this.ecMember.personal_data.birth_country] : null);
            this.form1.controls.birthMunicipality.setValue(MUNICIPALITIES.find(m => m.name === this.ecMember.personal_data.birth_municipality) ?? null);
            this.form1.controls.atecoCode.setValue(this.ecMember.personal_data.ateco_code);
            this.form1.controls.address.setValue(googleCompatibleTextAddress(this.ecMember.personal_data.address));
            this.currentAddress = this.ecMember.personal_data.address;
            this.currentCoordinates = this.ecMember.spatial_coordinates;
            this.accessTypeFormControl.setValue(this.ecMember.access_type);
            this.accessTypes = this.ecMember.participation_type === ECMemberParticipationType.THIRD_PARTY_PRODUCER ? [ECMemberAccessType.ADVANCED, ECMemberAccessType.NONE] : ACCESS_TYPES;
            this.documentTypes.pipe(takeUntil(this.destroyed))
                .subscribe(docTypes => {
                    this.ecMember.documents.forEach(doc => {
                            const documentForm = this.fb.group({
                                type: new FormControl(docTypes.includes(doc.document_type) ? doc.document_type : EC_DOCUMENT_TYPE_OTHER, [Validators.required]),
                                customType: new FormControl(docTypes.includes(doc.document_type) ? '' : doc.document_type, [Validators.pattern(DOCUMENT_TYPE_PATTERN)]),
                                id: new FormControl(doc.id, null),
                                fileName: new FormControl(doc.filename, null)
                            }, {
                                validators: [validateDocumentType]
                            });
                            this.documentsFormArray.push(documentForm);
                        }
                    );
                });


            this.ecMember.pods.forEach(pod => {
                const podRole = getPodRole(this.ecMember, pod.pod);
                const podForm = this.fb.group({
                    podId: new FormControl(null, []), // readonly
                    podMessage: new FormControl(null, []),
                    podCode: new FormControl(pod.pod, []), // readonly
                    podAddressLikeMember: new FormControl(false, []),
                    podAddress: new FormControl(googleCompatibleTextAddress(pod.address), []),
                    aspiringProsumer: new FormControl(pod.aspiring_prosumer, []),
                    // for internal use only
                    internalPodAddress: new FormControl(pod.address, []),
                    // for internal use only
                    internalPodCoordinates: new FormControl(pod.spatial_coordinates, []),
                    hasWifi: new FormControl(pod.has_wifi, []),
                    seu: new FormControl(isDefined(pod.seu) ? pod.seu : false, []),
                    podMemberRole: new FormControl(isDefined(podRole) ? podRole : null, [Validators.required]),
                    availablePodMemberRoles: new FormControl(this.getDefaultMemberRoles(), []),
                    podProvider: new FormControl(pod.energy_provider, []),
                    podActivationDate: new FormControl(pod.effective_date ? moment(pod.effective_date).startOf('day') : '', []),
                    podDeactivationDate: new FormControl(pod.deactivation_date ? moment(pod.deactivation_date).startOf('day') : '', []),
                    creationType: new FormControl(pod.creation_type, []),
                    landRegistrySection: new FormControl(pod.land_registry_data?.section, []),
                    landRegistrySheet: new FormControl(pod.land_registry_data?.sheet, []),
                    landRegistryParticle: new FormControl(pod.land_registry_data?.particle, []),
                    landRegistrySubdivision: new FormControl(pod.land_registry_data?.subdivision, []),
                    // third step
                    podEnergyProfile: new FormControl(PodEnergyProfiles.CONSUMER, []/*[Validators.required]*/),
                    podUtilityType: new FormControl(pod.pod_utility_type, []),
                    podVoltage: new FormControl(VoltageLevel.LOW, []/*[Validators.required]*/),
                    podPhase: new FormControl(Phases.SINGLE, []/*[Validators.required]*/),
                    contractualPower: new FormControl('', []),

                    profile: new FormControl(null, []),

                    plants: new FormArray([]),
                    // NB: if entityName is filled, energy parameters are not editable
                    entityName: new FormControl(null, []),
                    podName: new FormControl(null, []),
                    podEntityEnergyProfile: new FormControl(null, []),
                    podExists: new FormControl(null, []),
                    // if true pod is already saved into system
                    persistentPod: new FormControl(true, []),
                    predictiveModel: new FormControl(null, [])
                }, {
                    validator: (ctrls: AbstractControl) => this.validatePodDates(ctrls)
                });
                this.podsFormArray.push(podForm);
                this.managePodAddressLikeMemberChange(podForm);
                this.handleMemberRoleOnUpdate(pod.pod, podRole, this.ecMember.state, podForm);
            });
            this.form1.updateValueAndValidity();
        }
    }

    private listenForPodCodeChanges(podForm: FormGroup) {
        podForm.controls.podCode.valueChanges.pipe(
            takeUntil(this.destroyed),
            filter(pod => validatePodPattern(pod)), // start flow only when pod code is syntactically valid
            switchMap(pod => this.apiService.checkEntityExistenceByPOD(pod) // check if a pod with the given pod code already exists
                .pipe(switchMap(resp => this.getPodInfo(resp.value, pod)  // and get all the relevant information
                    .pipe(catchError(errorMessage => {
                            const disabledRoles = this.getDefaultMemberRoles()
                                .map(mr => ({...mr, disabled: true}));
                            podForm.controls.availablePodMemberRoles.setValue(disabledRoles);
                            podForm.controls.podExists.setValue(true);
                            podForm.controls.podCode.setErrors({[FormError.UNIQUE_POD]: true});
                            podForm.controls.podMemberRole.setValue(null);
                            podForm.controls.podMemberRole.disable();
                            return EMPTY;
                        })
                    ))))
        ).subscribe(podInfo => {
            const podCode = podForm.controls.podCode;
            const podMessage = podForm.controls.podMessage;
            const podMemberRole = podForm.controls.podMemberRole;
            const availablePodMemberRoles = podForm.controls.availablePodMemberRoles;

            // set initial state
            podCode.setErrors(null);
            podMessage.setValue(null);
            availablePodMemberRoles.setValue(this.getDefaultMemberRoles());
            podMemberRole.setValue(ECMemberRole.HOLDER);
            podMemberRole.enable();
            podForm.controls.podName.setValue(null);
            podForm.controls.podExists.setValue(null);
            podForm.controls.podEntityEnergyProfile.setValue(null);
            podForm.controls.seu.setValue(null);
            podForm.controls.seu.enable();

            // if podInfo is null -> pod doesn't already exist
            if (podInfo !== null) {
                // pod is a community pod or a member pod
                podForm.controls.podExists.setValue(true);
                podMessage.setValue(this.mkPodMessage(podInfo));
                if (isDefined(podInfo.namedPod)) {
                    const profile = getProfile(podInfo.namedPod);
                    podForm.controls.podEntityEnergyProfile.setValue(profile);
                    podForm.controls.podName.setValue(podInfo.namedPod.name);
                    this.setSeuIfPodExists(podForm, profile);
                }

                if (this.validatePodFormUniqueness(podCode)) {
                    podCode.setErrors({[FormError.UNIQUE_POD]: true});
                }

                if (this.isThirdPartyProducer && podInfo.namedPod.plants.some(ecPlant => ecPlant.plant.capital_grant > 0)) {
                    podCode.setErrors({[FormError.POD_HAS_CAPITAL_GRANT]: true});
                }
                if (isDefined(podInfo.memberInfo) && podInfo.memberInfo.length > 0) { // pod is a member pod
                    // disable role dropdown unavailable options
                    podInfo.memberInfo.forEach(info => availablePodMemberRoles.value.forEach(memberRole => {
                        if (memberRole.role === info.memberRole || memberRole.role === ECMemberRole.OWNER_AND_HOLDER) {
                            memberRole.disabled = true;
                        }
                    }));
                    // set role dropdown value if an option is available
                    podMemberRole.setValue(availablePodMemberRoles.value.find(mr => !mr.disabled)?.role ?? null);

                    if (podInfo.memberInfo.length === 1 && podInfo.memberInfo[0].memberRole === ECMemberRole.OWNER_AND_HOLDER ||
                        podInfo.memberInfo.length === 2 || // no roles available
                        podInfo.error) { // or pod is from another community
                        if (podInfo.error) {
                            const code = errorCode(podInfo.error);
                            switch (code) {
                                // we assume the user do not have permission to see the pod but the pod exists and has associations
                                case ErrorCode.INVALID_POD: {
                                    podCode.setErrors({[FormError.POD]: true});
                                    break;
                                }
                                case ErrorCode.POD_NOT_PART_OF_GIVEN_COMMUNITY: {
                                    podCode.setErrors({[FormError.UNIQUE_POD]: true});
                                    break;
                                }
                                default: {
                                    podCode.setErrors({[FormError.POD]: true});
                                }
                            }
                        } else {
                            podCode.setErrors({[FormError.POD_HAS_OWNER_OR_HOLDER]: true});
                        }

                        availablePodMemberRoles.value.forEach(mr => mr.disabled = true);
                        podMemberRole.setValue(null);
                        podMemberRole.disable();
                    }
                }
            } else {
                // pod is brand new and is not present in the back-end.
                // check for duplicates pod insertions.
                if (this.validatePodFormUniqueness(podCode)) {
                    podCode.setErrors({[FormError.UNIQUE_POD]: true});
                }
            }
        });
    }

    private validatePodFormUniqueness(podCode: AbstractControl) {
        const podsCount = this.podControls
            .map(ctrl => ctrl.controls.podCode.value)
            .reduce((pods, pod) => {
                pods[pod] = pods[pod] || {podCode: pod, count: 0};
                pods[pod].count++;
                return pods;
            }, {});

        return podsCount[podCode.value].count > 1;
    }

    private handleMemberRoleOnUpdate(pod: string, currentRole: ECMemberRole, state: ECMemberState, podForm: FormGroup) {
        // enable all roles
        podForm.controls.availablePodMemberRoles.setValue(this.getDefaultMemberRoles());
        // if the current role is owner and holder -> all roles must be available
        if (currentRole !== ECMemberRole.OWNER_AND_HOLDER) {
            // get relations of members related to the given pod
            this.apiService.checkMemberExistenceByPOD(pod).subscribe(membersAndRelations => {
                const assignedRelations = [].concat.apply([], Object.values(membersAndRelations));
                if (assignedRelations.length === 2) {
                    // all the relations are assigned, there are no available roles
                    podForm.controls.availablePodMemberRoles.setValue(Object.values(ECMemberRole).map(role => ({
                        role,
                        disabled: true
                    })));
                    podForm.controls.podMemberRole.disable();
                }
                // else
                // if there is only one already assigned relation -> it is the current relation -> all the roles must be available
                // if there are no already assigned relations -> all the roles must be available
            });
        }
        if (state === ECMemberState.REGISTERED && currentRole !== ECMemberRole.OWNER) {
            podForm.controls.availablePodMemberRoles.value
                .forEach((v: { role: ECMemberRole, disabled: boolean }) => {
                    if (v.role === ECMemberRole.OWNER) {
                        v.disabled = true;
                    }
                });
        }
    }

    private getPodInfo(exists: boolean, pod: string): Observable<PodInfo | null> {
        if (!exists) { // a pod with the given pod code doesn't already exist
            return of(null);
        }
        // a pod with the given pod code already exists
        // it could be a community pod or a member pod
        return this.apiService.checkMemberExistenceByPOD(pod)
            .pipe(switchMap(membersAndRelations => this.mkPodInfo(membersAndRelations, pod)));
    }

    private mkPodInfo(membersAndRelations: {
        [memberId: string]: ECMemberPODRelationType[]
    }, pod: string): Observable<PodInfo> {
        // since the given pod already exists, membersAndRelations value can be:
        // {} -> pod is a community pod
        // {id: [role]} -> pod has one relation with one member
        // {id: [owner, holder]}  -> pod has two relations with one member
        // {id1: [owner], id2: [holder]} -> pod has one relation with each of the two members
        const memberIds = Object.keys(membersAndRelations);
        if (memberIds.length === 0) {
            // pod is a community pod, pod name must be retrieved
            return this.apiService.findEntityByPOD(pod).pipe(
                switchMap(idResponse => this.apiService.getECPod(this.community.communityData.id, idResponse.id)),
                map(namedPod => ({namedPod})));
        }
        // pod is a member pod
        // if api returns a Result with an empty list then information about memberId can't be retrieved
        return this.apiService.findEntityByPOD(pod).pipe(
            switchMap(idResponse => {
                if (!isDefined(idResponse)) {
                    // we need more permission to retrieve information about the member
                    throw {
                        error: {
                            code: ErrorCode.INVALID_POD,
                            message: 'No permission to retrieve information about the member'
                        }
                    };
                }
                return this.apiService.getECPod(this.community.communityData.id, idResponse.id);
            }),
            switchMap(namedPod => this.apiService.findMembersByPOD(pod).pipe(
                    map(members => {
                        if (members.total === 0) {
                            return {memberInfo: [{memberRole: getMemberRole(membersAndRelations)}]};
                        }
                        const memberData = members.results.reduce((acc, member) => {
                            const {id, personal_data} = member;
                            acc[id] = `${personal_data.first_name} ${personal_data.last_name}`;
                            return acc;
                        }, {} as { [k: string]: string });
                        return {
                            namedPod,
                            memberInfo: memberIds.map(memberId => ({
                                memberRole: relationsToRoleConverter(membersAndRelations[memberId]),
                                memberName: memberData[memberId]
                            }))
                        };
                    })
                )
            ),
            catchError(responseError => {
                return of({
                    memberInfo: memberIds.map(memberId => ({memberRole: relationsToRoleConverter(membersAndRelations[memberId])})),
                    error: responseError as Error
                });
            })
        );
    }

    private mkPodMessage(info: PodInfo): string | null {
        if (isDefined(info.namedPod) && !isDefined(info.memberInfo)) {
            return this.i18n.translate('POD matches resource {0}', info.namedPod.name);
        }
        return info.memberInfo.map(memberInfo => this.infoToMessage(memberInfo)).join(',\n');
    }


    private infoToMessage(memberInfo: { memberRole: ECMemberRole; memberName?: string; }) {
        const role = this.genericPipe.transform(memberInfo.memberRole);
        const formattedRole = role.charAt(0).toLowerCase() + role.slice(1);
        if (isDefined(memberInfo.memberName)) {
            return this.i18n.translate('{0} is {1} of this POD', memberInfo.memberName, formattedRole);
        }
        if (memberInfo.memberRole === ECMemberRole.OWNER || memberInfo.memberRole === ECMemberRole.OWNER_AND_HOLDER) {
            return this.i18n.translate('This POD already has an {0}', formattedRole);
        }
        if (memberInfo.memberRole === ECMemberRole.HOLDER) {
            return this.i18n.translate('This POD already has a {0}', formattedRole);
        }
    }

    private updateForm(form: FormGroup) {
        for (const ctrl in form.controls) {
            if (form.controls.hasOwnProperty(ctrl)) {
                form.controls[ctrl].updateValueAndValidity({onlySelf: true, emitEvent: true});
            }
        }
        form.updateValueAndValidity({onlySelf: true, emitEvent: true});
    }

    public foreignCountry(): boolean {

        if (isDefined(this.currentAddress)) {
            if (this.currentAddress.region === null && this.currentAddress.country !== null) {
                // custom address case we do not know if the address has a foreing country or not
                // we assume it to be in Italy
                return false;
            }
            return isDefined(this.currentAddress.country) &&
                !ALLOWED_COUNTRIES.includes(this.currentAddress.country);
        }

        return false;
    }

    private managePodAddressLikeMemberChange(podForm: FormGroup) {
        podForm.controls.podAddressLikeMember.valueChanges.pipe(takeUntil(this.destroyed)).subscribe(
            value => {
                if (value) {
                    podForm.controls.podAddress.disable();
                    podForm.controls.podAddress.clearValidators();
                    podForm.controls.podAddress.setValue(this.currentAddress ? googleCompatibleTextAddress(this.currentAddress) : this.addressFormControl.value);
                    podForm.controls.internalPodCoordinates.setValue(null);
                    podForm.controls.internalPodAddress.setValue(null);
                } else {
                    podForm.controls.podAddress.enable();
                    podForm.controls.podAddress.setValidators([Validators.required]);
                    podForm.controls.podAddress.setValue(null);
                }
            }
        );
    }

    private getStatProfile(podForm: FormGroup): Promise<{
        predictiveModel?: PredictiveModelInfo,
        profile?: PodStatisticalProfile
    } | void> {
        const energyProfile = podForm.controls.podEnergyProfile.value as PodEnergyProfiles;
        if (energyProfile === PodEnergyProfiles.CONSUMER) {
            return this.apiService.getConsumerPodPredictiveModel({}).toPromise()
                .then(response => {
                    podForm.controls.predictiveModel.setValue(response);
                    return {profile: podForm.controls.profile.value, predictiveModel: response};
                }).catch(err => {
                    console.error(err);
                    this.msgBoxService.error(this.i18n.translate('Cannot generate predictive model for this POD'));
                });
        }
        if (energyProfile === PodEnergyProfiles.PROSUMER) {
            return this.apiService.getProsumerPodPredictiveModel({}).toPromise()
                .then(response => {
                    podForm.controls.predictiveModel.setValue(response);
                    return {profile: podForm.controls.profile.value, predictiveModel: response};
                }).catch(err => {
                    console.error(err);
                    this.msgBoxService.error(this.i18n.translate('Cannot generate predictive model for this POD'));
                });
        }
        if (energyProfile === PodEnergyProfiles.PRODUCER) {
            const plantsStatisticalProfiles = this.plantControls(podForm)
                .map(form => ({
                    type: getPodStatisticalProfileRequestPlantType(form.controls.plantType.value),
                    profile: form.controls.profile.value
                }));
            const req: ProducerPodStatisticalProfileRequest = {
                plants: plantsStatisticalProfiles,
                rate_mode: getRateMode(plantsStatisticalProfiles)
            };
            return this.apiService.getProducerPodStatisticalProfile(req).toPromise()
                .then(response => {
                    podForm.controls.predictiveModel.setValue(null);
                    podForm.controls.profile.setValue(response);
                    return {profile: response, predictiveModel: null};
                }).catch(err => {
                    console.error(err);
                    this.msgBoxService.error(this.i18n.translate('Cannot generate statistical profile for this POD'));
                });
        }
        return Promise.resolve({predictiveModel: null, profile: null});
    }

    private resetPodStatisticalProfile(podForm: FormGroup): void {
        podForm.controls.profile.setValue(null);
    }

    private validatePodDates(ctrl: AbstractControl): ValidationErrors | null {
        const errs: ValidationErrors = {};
        const ctrls: { [ctrl: string]: AbstractControl } = (ctrl as FormGroup).controls;
        // and POD effective date(s)/deactivation date(s) validity (only if the corresponding pod is not blank)
        const effectiveDate = ctrls.podActivationDate.value !== '' ? ctrls.podActivationDate.value : null;
        const deactivationDate = ctrls.podDeactivationDate.value !== '' ? ctrls.podDeactivationDate.value : null;
        if (isDefined(effectiveDate) && isDefined(deactivationDate) && !fromBeforeTo(effectiveDate, deactivationDate)) {
            errs.pod_date_mismatch = true;
        }
        return errs;
    }

    private podCode(podForm: FormGroup): string {
        return podForm.controls.podCode.value;
    }

    private plantsFormArray(podForm: FormGroup): FormArray {
        return podForm.controls.plants as FormArray;
    }

    /**
     * Calculate plant statistical profile and predictive model on nominalPower change
     * @param plantForm
     */
    private updatePlantStatAndPredModOnChange(plantForm: FormGroup) {
        plantForm.controls.nominalPower.valueChanges
            .pipe(takeUntil(this.destroyed),
                filter(isDefined),
                tap(() => this.step3Busy = true),
                debounceTime(500),
                switchMap(nominalPower => forkJoin([
                    this.mkPlantDefaultStatisticalProfile(nominalPower, RateMode.SINGLE, getPlantType(plantForm)),
                    this.mkPlantDefaultPredictiveModel(nominalPower, getPlantType(plantForm))])))
            .subscribe(([plantStatisticalProfile, plantPredictiveModel]) => {
                if (plantForm.controls.plantType.value === PV_PLANT_TYPE) {
                    plantForm.controls.profile.setValue(plantStatisticalProfile);
                } else if (plantForm.controls.plantType.value === WIND_PLANT_TYPE) {
                    plantForm.controls.profile.reset();
                }
                plantForm.controls.predictiveModel.setValue(plantPredictiveModel);
                this.step3Busy = false;
            });

        plantForm.controls.plantType.valueChanges
            .pipe(takeUntil(this.destroyed),
                filter(isDefined),
                tap(() => this.step3Busy = true),
                debounceTime(500),
                switchMap(() => forkJoin([
                    this.mkPlantDefaultStatisticalProfile(plantForm.controls.nominalPower.value, RateMode.SINGLE, getPlantType(plantForm)),
                    this.mkPlantDefaultPredictiveModel(plantForm.controls.nominalPower.value, getPlantType(plantForm))])))
            .subscribe(([plantStatisticalProfile, plantPredictiveModel]) => {
                if (plantForm.controls.plantType.value === PV_PLANT_TYPE) {
                    plantForm.controls.profile.setValue(plantStatisticalProfile);
                } else if (plantForm.controls.plantType.value === WIND_PLANT_TYPE) {
                    plantForm.controls.profile.reset();
                }
                plantForm.controls.predictiveModel.setValue(plantPredictiveModel);
                this.step3Busy = false;
            });
    }

    private mkPlantDefaultStatisticalProfile(nominalPower: number, rateMode: RateMode, type: PlantType): Observable<PlantStatisticalProfile> {
        switch (type) {
            case WIND_PLANT_TYPE:
                return this.apiService.getWindPlantStatisticalProfile({
                    nominal_power: nominalPower,
                    rate_mode: rateMode
                });
            case PV_PLANT_TYPE:
            default:
                return this.apiService.getPvPlantStatisticalProfile({
                    nominal_power: nominalPower,
                    rate_mode: rateMode,
                    zone: this.community.community.zone
                });
        }
    }

    private mkPlantDefaultPredictiveModel(nominalPower: number, type: PlantType): Observable<PredictiveModelInfo> {
        switch (type) {
            case WIND_PLANT_TYPE:
                return this.apiService.getWindPlantPredictiveModel({});
            case PV_PLANT_TYPE:
            default:
                return this.apiService.getPvPlantPredictiveModel({nominal_power: nominalPower});
        }
    }

    /**
     * search existing resource pod and link it to administrative pod
     * @private
     */
    private mkPromises() {
        return Promise.all(this.podControls.map(podForm => {
            const pod = podForm.controls.podCode.value;
            return this.apiService.findEntityByPOD(pod).toPromise().then(result => {
                if (result) {
                    this.hierarchyService.findEntityById(result.id).pipe(first()).toPromise()
                        .then(entity => podForm.controls.entityName.setValue(entity.name));
                } else {
                    if (this.isUpdate && this.isPersistentPod(podForm)) {
                        podForm.controls.entityName.setValue(podForm.controls.podCode.value);
                    } else {
                        podForm.controls.entityName.setValue(null);
                    }
                }
            });
        })).then(() => {
            this.refreshSteps3Enabled();
        });
    }

    private changeEnergyProfile() {
        if (this.isThirdPartyProducer) {
            if (this.hasSEU) {
                this.podControls.forEach(podForm => {
                    podForm.controls.podEnergyProfile.setValue(PodEnergyProfiles.PROSUMER);
                    podForm.controls.podEnergyProfile.disable();
                });
            } else {
                this.podControls.forEach(podForm => {
                    podForm.controls.podEnergyProfile.setValue(PodEnergyProfiles.PRODUCER);
                    podForm.controls.podEnergyProfile.disable();
                });
            }
        } else {
            this.podControls.forEach(podForm => {
                podForm.controls.seu.setValue(null);
                podForm.controls.podEnergyProfile.setValue(this.getDefaultEnergyProfile());
                podForm.controls.podEnergyProfile.enable();
            });
        }
    }

    private disableUtilityType() {
        this.podControls
            .filter(podForm => this.isProducerResource(podForm))
            .forEach(podForm => {
                podForm.controls.podUtilityType?.setValue('');
                podForm.controls.podUtilityType?.disable();
            });
    }

    private async createMember() {
        const sendInvitation = this.form1.controls.sendInvitation.value;
        const personalData: ECMemberPersonalData = {
            email: this.form1.controls.email.value && this.form1.controls.email.value !== '' ? this.form1.controls.email.value : null,
            company_name: this.isCompany ? this.form1.controls.companyName.value : null,
            first_name: this.form1.controls.firstName.value,
            last_name: this.form1.controls.lastName.value,
            cf: mkTextField(this.form1.controls.fiscalCode.value),
            piva: this.isCompany ? mkTextField(this.form1.controls.vatNumber.value) : null,
            pec: mkTextField(this.form1.controls.pec.value),
            iban: mkTextField(this.form1.controls.iban.value),
            ateco_code: mkTextField(this.form1.controls.atecoCode.value),
            birth_date: this.form1.controls.birthDate.value ? this.form1.controls.birthDate.value.valueOf() : null,
            birth_country: this.form1.controls.birthCountry.value?.code,
            birth_municipality: this.form1.controls.birthMunicipality.value?.name,
            address: this.currentAddress,
            mobile_number: mkTextField(this.form1.controls.mobileNumber.value),
            telephone_number: mkTextField(this.form1.controls.phoneNumber.value),
        };
        const asyncPods = this.podControls.map(podForm => this.mkEcMemberPodPromise(podForm));
        const pods = await Promise.all(asyncPods);

        const member: NewECMemberRequest = {
            network_id: this.community.communityData.id,
            juridical_type: this.juridicalType,
            legal_form: this.legalFormType,
            subject_type: this.subjectType,
            entity_subtype: this.entitySubtype,
            personal_data: personalData, // ECMemberPersonalData,
            spatial_coordinates: this.currentCoordinates, // SpatialCoordinates
            pods,
            pod_relations: this.mkPodRelations(),
            participation_type: this.participationType,
            access_type: this.getAccessType,
            signatory_tax_code: mkTextField(this.form1.controls.signatoryTaxCode.value)
        };
        const spinner = this.openSpinner('We are creating the subject');
        try {
            const response = await this.apiService.createMember(member);
            if (sendInvitation) {
                await this.apiService.inviteMember(response.id)
                    .catch(err => {
                        this.msgBoxService.error(this.i18n.translate('Could not send invitation to subject'), err);
                    });
            }
            if (Object.keys(this.documents).length > 0) {
                await this.uploadDocuments(response.id);
            }
            this.closeDialog.emit(true);
            spinner.context.next({
                loading: false,
                loadingMessage: this.i18n.translate('Subject successfully created')
            });
            spinner.closed.then(() => {
                this.dialogService.open<any, any>({
                    template: this.adviceDialog
                });
            });
        } catch (err) {
            const errMsg = this.i18n.translate(mkErrorMessage(err, 'An error occurred while creating subject'));
            spinner.context.next({
                loading: false,
                loadingMessage: errMsg,
                abort: true
            });
            this.msgBoxService.error(errMsg, err);
            throw err;
        }
    }

    private uploadDocuments(memberId: string): Promise<(ECMemberDocument | void)[]> {
        return Promise.all(this.documentControls.map(form => {
            const fileName = form.controls.fileName.value;
            const type = form.controls.type.value;
            const customType = form.controls.customType.value;
            const id = form.controls.id.value;
            const document = this.documents[id];
            const file = {filename: fileName, content: document.file};
            // if the provided type matches one of the suggested types, use it as the type
            // otherwise, use the type inserted by the user in the custom type field
            return this.apiService.createMemberDocument(memberId, file,
                type === EC_DOCUMENT_TYPE_OTHER ? customType : type)
                .catch(err => {
                    console.error(`Cannot upload file: ${fileName}`, err);
                });
        }));
    }

    private prepareDocuments(): EditedDocument[] {
        return this.documentControls.map(docForm => {
            const docId = docForm.controls.id.value;
            const type = docForm.controls.type.value;
            const customType = docForm.controls.customType.value;
            return {
                documentId: docId,
                documentType: type === EC_DOCUMENT_TYPE_OTHER ? customType : type,
                file: this.documents[docId]?.file
            };
        });
    }

    private updateMemberDocuments(member: ECMember, editedDocs: EditedDocument[]): Promise<ECMember> {
        const newDocuments = editedDocs.filter(doc => doc.documentId.startsWith(INTERNAL_DOCUMENT_PREFIX));
        const updatedDocuments = editedDocs.filter(doc => !doc.documentId.startsWith(INTERNAL_DOCUMENT_PREFIX) && !isSameDocument(doc, member.documents.find(m => m.id === doc.documentId)));
        const removedDocuments = member.documents.filter(doc => !editedDocs.find(ed => ed.documentId === doc.id));
        if (!newDocuments.length && !updatedDocuments.length && !removedDocuments.length) {
            return Promise.resolve(member);
        }
        // NOTE: after all changes have been committed, the member is reloaded
        return Promise.all([
            // create new documents
            ...(newDocuments.map(doc => this.apiService.createMemberDocument(member.id, {
                content: doc.file,
                filename: doc.file.name
            }, doc.documentType, doc.metadata, doc.expiration).then(() => {
            }))),
            // update already existing documents
            ...(updatedDocuments.map(doc => this.apiService.updateMemberDocument(member.id, doc.documentId,
                doc.file ? {
                    content: doc.file,
                    filename: doc.file.name
                } : null, doc.documentType, doc.metadata, doc.expiration))),
            // delete removed documents
            ...(removedDocuments.map(doc => this.apiService.deleteMemberDocument(member.id, doc.id)))
        ]).then(() => this.apiService.loadMember(member.id).toPromise());
    }

    /**
     * createEcMemberPod for existent resources
     * @param podForm
     * @param energyCommunityNamedPod
     */
    private createEcMemberPod(podForm: FormGroup, energyCommunityNamedPod: EnergyCommunityNamedPod): ECMemberPOD {
        const podActivationDate = podForm.controls.podActivationDate.value;
        const podDeactivationDate = this.isUpdate ? podForm.controls.podDeactivationDate.value : null;
        const podAddressLikeMember = podForm.controls.podAddressLikeMember.value as boolean;
        const podAddress = podAddressLikeMember ? this.currentAddress : podForm.controls.internalPodAddress.value;
        const podCoordinates = podAddressLikeMember ? this.currentCoordinates : podForm.controls.internalPodCoordinates.value;
        const creationType = podForm.controls.creationType.value || ECPodCreationType.MANUAL;
        const aspiringProsumer = podForm.controls.aspiringProsumer.value;
        return {
            pod: podForm.controls.podCode.value,
            effective_date: podActivationDate ? podActivationDate.valueOf() : null, // number; ?
            deactivation_date: podDeactivationDate ? podDeactivationDate.valueOf() : null, // TODO add for update
            pod_utility_type: podForm.controls.podUtilityType.value, // ECPodUtilityType; ?
            address: podAddress, // ECMemberAddress; ?
            has_wifi: podForm.controls.hasWifi.value, // boolean;
            seu: podForm.controls.seu.value, // boolean
            energy_provider: podForm.controls.podProvider.value, // string; ?
            land_registry_data: mkLandRegistryData(podForm),
            spatial_coordinates: podCoordinates, // SpatialCoordinates; ?
            resource: energyCommunityNamedPod,
            creation_type: creationType,
            aspiring_prosumer: aspiringProsumer
        };
    }

    /**
     * create an object createEcMemberPod with resources
     * @param podForm
     */
    private async createEcMemberPodFull(podForm: FormGroup): Promise<ECMemberPOD> {
        const profiles = await this.getStatProfile(podForm);
        const plants: EnergyCommunityNamedPlant[] = this.plantControls(podForm)
            .map(formPlant => ({
                id: null,
                name: formPlant.controls.plantName.value,
                plant: {
                    type: EntityType.PLANT,
                    sub_type: getEntitySubtype(formPlant.controls.plantType.value),
                    voltage: podForm.controls.podVoltage.value,
                    phases: podForm.controls.podPhase.value,
                    nominal_power: formPlant.controls.nominalPower.value,
                    activation_date: formPlant.controls.enterData.value ? formPlant.controls.enterData.value.valueOf() : null,
                    incentive_mode: formPlant.controls.incentiveType.value,
                    statistical_profile: formPlant.controls.profile.value,
                    production_pod: !isBlank(formPlant.controls.podCode.value) ? formPlant.controls.podCode.value : null,
                    predictive_models: [formPlant.controls.predictiveModel.value],
                    capital_grant:
                        this.isThirdPartyProducer ||
                        isDefined(formPlant.controls.enterData.value) &&
                        (formPlant.controls.enterData.value as Moment).isBefore(moment.tz(UPPER_INCENTIVE_BOUNDARY, this.timeZone)) ?
                            0.0 :
                            formPlant.controls.capitalGrant.value / 100
                }
            }));
        const newPod: Pod = {
            type: EntityType.POD,
            voltage: podForm.controls.podVoltage.value,
            phases: podForm.controls.podPhase.value,
            producer: podForm.controls.podEnergyProfile.value === PodEnergyProfiles.PRODUCER,
            contractual_power: podForm.controls.contractualPower ? podForm.controls.contractualPower.value : null,
            predictive_models: !!profiles && !!profiles.predictiveModel ? [profiles.predictiveModel] : null,
            statistical_profile: !!profiles && !!profiles.profile ? profiles.profile : null,
            metadata: {
                [ENTITY_METADATA.POD_KEY]: this.podCode(podForm)
            }
        };
        const energyCommunityNamedPod: EnergyCommunityNamedPod = {
            id: null,
            name: podForm.controls.podCode.value,
            pod: newPod,
            plants
        };
        return this.createEcMemberPod(podForm, energyCommunityNamedPod);
    }

    private async updateMember(): Promise<void> {
        const sendInvitation = this.form1.controls.sendInvitation.value;
        const personalData: ECMemberPersonalData = {
            email: this.ecMember.personal_data.email ? this.ecMember.personal_data.email : this.form1.controls.email.value && this.form1.controls.email.value !== '' ? this.form1.controls.email.value : null,
            company_name: this.isCompany ? this.form1.controls.companyName.value : null,
            first_name: this.form1.controls.firstName.value,
            last_name: this.form1.controls.lastName.value,
            cf: mkTextField(this.form1.controls.fiscalCode.value),
            piva: this.isCompany ? mkTextField(this.form1.controls.vatNumber.value) : null,
            pec: mkTextField(this.form1.controls.pec.value),
            iban: mkTextField(this.form1.controls.iban.value),
            ateco_code: mkTextField(this.form1.controls.atecoCode.value),
            birth_date: this.form1.controls.birthDate.value ? this.form1.controls.birthDate.value.valueOf() : null,
            birth_country: this.form1.controls.birthCountry.value?.code,
            birth_municipality: this.form1.controls.birthMunicipality.value?.name,
            address: this.currentAddress,
            mobile_number: mkTextField(this.form1.controls.mobileNumber.value),
            telephone_number: mkTextField(this.form1.controls.phoneNumber.value)
        };
        if (this.form1.controls.entitySubtype.value !== this.ecMember.entity_subtype) {
            // If we changed the subtype, then we need to change the pod utility type of all user pods
            // to the ones that are actually supported by the new subtype
            const availableArr = getPodUtilityTypes(this.juridicalType,
                this.form1.controls.legalForm.value, this.form1.controls.subjectType.value,
                this.form1.controls.entitySubtype.value);
            if (!availableArr.includes(ECPodUtilityType.OTHER)) {  // if new subtype doesn't include other, then change it
                this.podControls.forEach(podForm => {
                    podForm.controls.podUtilityType.setValue(availableArr[0]);  // take the first one available
                });
            }
        }
        let hasNewPods = false;
        const asyncPods = this.podControls
            .map(podForm => {
                if (this.isPersistentPod(podForm)) {
                    return Promise.resolve(this.createEcMemberPod(podForm, null));
                } else {
                    hasNewPods = true;
                    return this.mkEcMemberPodPromise(podForm);
                }
            });
        const pods = await Promise.all(asyncPods);

        const request: UpdateECMemberRequest = {
            u3id: this.ecMember.u3id,
            network_id: this.community.communityData.id,
            juridical_type: this.ecMember.juridical_type,
            legal_form: this.ecMember.legal_form,
            subject_type: this.ecMember.subject_type,
            entity_subtype: this.ecMember.entity_subtype || this.form1.controls.entitySubtype.value,
            personal_data: personalData, // ECMemberPersonalData,
            accepted_documents: this.ecMember.accepted_documents,
            spatial_coordinates: this.currentCoordinates, // SpatialCoordinates
            pods,
            pod_relations: this.mkPodRelations(),
            participation_type: this.ecMember.participation_type,
            access_type: this.getAccessType,
            signatory_tax_code: mkTextField(this.form1.controls.signatoryTaxCode.value)
        };
        const spinner = this.openSpinner('We are updating the subject');
        try {
            await this.apiService.updateMember(this.ecMember.id, request, true);
            if (sendInvitation) {
                await this.apiService.inviteMember(this.ecMember.id)
                    .catch(err => {
                        this.msgBoxService.error(this.i18n.translate('Could not send invitation to subject'), err);
                    });
            }

            await this.updateMemberDocuments(this.ecMember, this.prepareDocuments());

            this.closeDialog.emit(true);
            spinner.context.next({
                loading: false,
                loadingMessage: this.i18n.translate('Subject updated')
            });
            spinner.closed.then(() => {
                if (hasNewPods) {
                    this.dialogService.open<any, any>({
                        template: this.adviceDialog
                    });
                }
            });
        } catch (err) {
            const errMsg = this.i18n.translate(mkErrorMessage(err, 'An error occurred while updating subject'));
            spinner.context.next({
                loading: false,
                loadingMessage: errMsg,
                abort: true
            });
            this.msgBoxService.error(errMsg, err);
            throw err;
        }
    }

    private openSpinner(message: string) {
        const spinnerData = new BehaviorSubject<SpinnerData>({
            loading: true,
            loadingMessage: this.i18n.translate(message)
        });
        return this.dialogService.open<Subject<SpinnerData>, undefined>({
            template: this.spinnerDialog,
            context: spinnerData
        }, {
            width: 700,
            hideCloseButton: true
        });
    }

    private mkPlantPodValidator(): ValidatorFn {
        return ctrl => {
            const podErr = productionPODValidator(ctrl);
            if (podErr) {
                return podErr;
            }
            if (isBlank(ctrl.value)) {
                // no need to check uniqueness
                return null;
            }
            // check for production POD uniqueness
            const otherProductionPods: string[] = [];
            this.podControls.forEach(podForm => this.plantControls(podForm).forEach(plantForm => {
                if (plantForm.controls.podCode !== ctrl) {
                    otherProductionPods.push(plantForm.controls.podCode.value);
                }
            }));
            if (otherProductionPods.find(p => p?.trim() === ctrl.value)) {
                return {[FormError.UNIQUE_POD]: true};
            }
            return null;
        };
    }

    private setAccessTypesByEnergyProfiles() {
        const atLeastOnePodProducer = this.podControls
            .map(podForm => {

                    const isProducer = (podForm.controls.podEnergyProfile.value as PodEnergyProfiles) === PodEnergyProfiles.PRODUCER ||
                        (podForm.controls.podEntityEnergyProfile.value as PodEnergyProfiles) === PodEnergyProfiles.PRODUCER;

                    const isSEU = ((podForm.controls.podEnergyProfile.value as PodEnergyProfiles) === PodEnergyProfiles.PROSUMER ||
                            (podForm.controls.podEntityEnergyProfile.value as PodEnergyProfiles) === PodEnergyProfiles.PROSUMER) &&
                        (podForm.controls.seu.value as boolean) === true;
                    return isProducer || isSEU;
                }
            ).reduce((acc, val) => acc || val, false);
        this.setAccessTypes(atLeastOnePodProducer ? ECMemberParticipationType.THIRD_PARTY_PRODUCER : null);
    }

    private setAccessTypes(participationType?: ECMemberParticipationType) {
        if (this.featureConfig?.advanced_user && participationType && participationType === ECMemberParticipationType.THIRD_PARTY_PRODUCER) {
            this.accessTypes = [ECMemberAccessType.ADVANCED, ECMemberAccessType.NONE];
        } else {
            this.accessTypes = this.featureConfig?.advanced_user ? ACCESS_TYPES : [ECMemberAccessType.MOBILE, ECMemberAccessType.NONE];
        }
    }

    private setSeuIfPodExists(podForm: FormGroup, profile: PodEnergyProfiles) {
        if (profile === PodEnergyProfiles.PRODUCER || profile === PodEnergyProfiles.CONSUMER) {
            podForm.controls.seu.setValue(false);
            podForm.controls.seu.disable();
        } else if (profile === PodEnergyProfiles.PROSUMER) {
            podForm.controls.seu.setValue(true);
            podForm.controls.seu.disable();
        }
    }

    private mkPodRelations(): ECMemberPODRelation[] {
        const result = this.podControls
            .filter(podForm => isDefined(podForm.controls.podMemberRole.value))
            .map(podForm => roleToRelationConverter(podForm.controls.podCode.value, podForm.controls.podMemberRole.value));
        return [].concat.apply([], result); // flat
    }

    private mkEcMemberPodPromise(podForm: FormGroup): Promise<ECMemberPOD> {
        if (podForm.controls.entityName.value) {
            return Promise.resolve(this.createEcMemberPod(podForm, null));
        } else {
            return this.createEcMemberPodFull(podForm);
        }
    }

    private mkPlantName(type: any, counter: number) {
        const typeName = `${type.name}__{resource}`;
        return `${this.i18n.translate(typeName)} ${counter}`;
    }

    private getDefaultMemberRoles() {
        return Object.values(ECMemberRole).map(role => ({role, disabled: false}));
    }

}


function validateForm(ctrl: AbstractControl): ValidationErrors | null {
    const errs: ValidationErrors = {};
    const ctrls: { [ctrl: string]: AbstractControl } = (ctrl as FormGroup).controls;
    // company name only mandatory for `COMPANY` members
    if (!(ctrls.juridicalType.value === ECJuridicalType.INDIVIDUAL && ctrls.legalForm.value === ECLegalForm.OTHER_INDIVIDUALS)
        && !mkTextField(ctrls.companyName.value)) {
        ctrls.companyName.setErrors({[FormError.EMPTY]: true});
    } else {
        ctrls.companyName.setErrors(null);
    }
    if (ctrls.juridicalType.value === ECJuridicalType.INDIVIDUAL && !ctrls.legalForm.value) {
        ctrls.legalForm.setErrors({[FormError.EMPTY]: true});
    } else {
        ctrls.legalForm.setErrors(null);
    }
    if (ctrls.juridicalType.value === ECJuridicalType.CORPORATE_ENTITY &&
        (!ctrls.subjectType.value)) {
        ctrls.subjectType.setErrors({[FormError.EMPTY]: true});  // there is no actual visibility of these errors aside from the asterisks (field mandatory)
    } else {
        ctrls.subjectType.setErrors(null);
    }
    if (mkTextField(ctrls.email.value) !== mkTextField(ctrls.confirmEmail.value)) {
        ctrls.confirmEmail.setErrors({[FormError.NOT_MATCHING_EMAILS]: true});
    } else {
        ctrls.confirmEmail.setErrors(null);
    }
    return Object.values(errs).every(err => !err) ? null : errs;
}

function mkStatisticalProfilePodType(energyProfile: PodEnergyProfiles): StatisticalProfilePodResourceType {
    switch (energyProfile) {
        case PodEnergyProfiles.CONSUMER:
            return StatisticalProfilePodResourceType.CONSUMER;
        case PodEnergyProfiles.PRODUCER:
            return StatisticalProfilePodResourceType.PRODUCER;
        case PodEnergyProfiles.PROSUMER:
            return StatisticalProfilePodResourceType.PROSUMER;
    }
}

function mkErrorMessage(err: Error, fallback: string): string {
    const code = errorCode(err);
    switch (code) {
        case ErrorCode.POD_CONNECTED_TO_NEW_ENTITY_NOT_IN_COMMUNITY:
        case ErrorCode.POD_CONNECTED_TO_ENTITY_NOT_IN_COMMUNITY:
            return 'POD connects resource to a member that is not part of this Energy Community';
        case ErrorCode.MEMBER_EMAIL_ALREADY_EXISTING:
            return 'A subject with this email already exists';
        case ErrorCode.MEMBER_EMAIL_ALREADY_EXISTING_FOR_USER:
            return 'A user with this email already exists';
        case ErrorCode.MEMBER_MOBILE_ALREADY_EXISTING:
            return 'A subject with this mobile number already exists';
        case ErrorCode.MEMBER_MOBILE_ALREADY_EXISTING_FOR_USER:
            return 'A user with this mobile number already exists';
        case ErrorCode.MISSING_REQUIRED_VALUES:
            return 'A required value is missing';
        case ErrorCode.INVALID_POD_ACTIVATION_DATE:
            return 'Invalid POD activation date';
        case ErrorCode.INVALID_FILE_TYPE:
            return 'Invalid file type';
        case ErrorCode.INVALID_MEMBER_JURIDICAL_TYPE:
            return 'Invalid energy community member juridical type provided';
        case ErrorCode.INVALID_POD_UTILITY_TYPE:
            return 'Invalid POD utility type provided';
        case ErrorCode.INVALID_DATE:
            return 'Invalid date provided';
        case ErrorCode.POD_ALREADY_IN_RELATION_WITH_SUBJECT:
            return 'This POD is already in a relation with a subject';
        default:
            return fallback;
    }
}

function isSameDocument(editedDoc: EditedDocument, doc: ECMemberDocument): boolean {
    return !editedDoc.file &&
        editedDoc.documentType === doc.document_type &&
        _.isEqual(editedDoc.metadata || {}, doc.metadata || {}) &&
        sameExpiration(editedDoc.expiration, doc.expire_date);
}

function sameExpiration(exp1: number, exp2: number): boolean {
    return (isDefined(exp1) ? exp1 : null) === (isDefined(exp2) ? exp2 : null);
}

function fromBeforeTo(fromDate: Moment, toDate: Moment): boolean {
    return fromDate.isBefore(toDate);
}

function getProfile(pod: EnergyCommunityNamedPod): PodEnergyProfiles {
    if (pod.plants && pod.plants.length > 0) {
        if (pod.pod.producer) {
            return PodEnergyProfiles.PRODUCER;
        } else {
            return PodEnergyProfiles.PROSUMER;
        }
    }
    return PodEnergyProfiles.CONSUMER;
}

function mkLandRegistryData(podForm: FormGroup): ECLandRegistryData | null {
    const section = mkTextField(podForm.controls.landRegistrySection.value);
    const sheet = mkTextField(podForm.controls.landRegistrySheet.value);
    const particle = mkTextField(podForm.controls.landRegistryParticle.value);
    const subdivision = mkTextField(podForm.controls.landRegistrySubdivision.value);
    return !!section || !!sheet || !!particle || !!subdivision ? {section, sheet, particle, subdivision} : null;
}


